import React from "react";
import filterData from "../services/filterData";
import { Box } from "../styles/Box";
import { Heading } from "../styles/Heading";
import { Paragraph } from "../styles/Paragraph";
import { greenScale, primaryColorScales } from "../theme/styles";
import { PensionCalculatorResponse } from "../types";

type Props = {
  averageMonthlySalary: number;
  yearlySalary: number;
  selectInput: string;
  data: PensionCalculatorResponse[];
};

const Table: React.FC<Props> = ({
  averageMonthlySalary,
  yearlySalary,
  selectInput,
  data,
}) => {
  const collection = filterData({ data });

  const fixedSumForMaxPremium =
    parseInt(collection.fixedSumForMaxPremium) || 28148;
  const salaryLimit = parseInt(collection.salaryLimit) || 625500;
  const salaryCapKapKl = parseInt(collection.salaryCapKapKl) || 2229000;

  const yearlySalaryUpTo = (salaryPerYear: number): string => {
    return Math.round(salaryPerYear * 0.045)
      .toLocaleString()
      .replace(",", " ");
  };

  const yearlySalaryOver = (salaryPerYear: number): string => {
    return Math.round((salaryPerYear - salaryLimit) * 0.3)
      .toLocaleString()
      .replace(",", " ");
  };

  const yearlySalaryUpToAkap = (salaryPerYear: number): string => {
    return salaryPerYear < salaryLimit
      ? Math.round(salaryPerYear * 0.06)
          .toLocaleString()
          .replace(",", " ")
      : Math.round(salaryLimit * 0.06)
          .toLocaleString()
          .replace(",", " ");
  };
  const yearlySalaryOverAkap = (salaryPerYear: number): string => {
    return salaryPerYear > salaryLimit
      ? Math.round((salaryPerYear - salaryLimit) * 0.315)
          .toLocaleString()
          .replace(",", " ")
      : (0).toLocaleString().replace(",", " ");
  };

  const yearlySalaryUpToKTP = (salaryPerYear: number): string => {
    return salaryPerYear < salaryLimit
      ? Math.round(salaryPerYear * 0.063)
          .toLocaleString()
          .replace(",", " ")
      : Math.round(salaryLimit * 0.063)
          .toLocaleString()
          .replace(",", " ");
  };
  const yearlySalaryOverKTP = (salaryPerYear: number): string => {
    return salaryPerYear > salaryLimit
      ? Math.round((salaryPerYear - salaryLimit) * 0.318)
          .toLocaleString()
          .replace(",", " ")
      : (0).toLocaleString().replace(",", " ");
  };

  const annualPremium = (salaryPerYear: number): string | undefined => {
    let premium;

    if (selectInput === "Kommun och region AKAP-KR") {
      if (salaryPerYear <= salaryLimit) {
        premium = Math.round(salaryPerYear * 0.06);
      } else {
        premium = Math.round(
          salaryLimit * 0.06 + (salaryPerYear - salaryLimit) * 0.315,
        );
      }
    } else if (selectInput === "Kommun och region KAP-KL") {
      if (salaryPerYear < salaryCapKapKl) {
        premium = Math.round(salaryPerYear * 0.045);
      } else {
        premium = Math.round(salaryCapKapKl * 0.045);
      }
    } else if (
      selectInput === "Anställda inom kooperationen KTP1 AFO-Handels"
    ) {
      if (salaryPerYear <= salaryLimit) {
        premium = Math.round(salaryPerYear * 0.063);
      } else {
        premium = Math.round(
          salaryLimit * 0.063 + (salaryPerYear - salaryLimit) * 0.318,
        );
      }
    } else {
      if (salaryPerYear <= salaryLimit) {
        premium = Math.round(salaryPerYear * 0.045);
      } else if (salaryPerYear > salaryLimit) {
        premium = Math.round(
          (salaryPerYear - salaryLimit) * 0.3 + fixedSumForMaxPremium,
        );
      }
    }
    return premium?.toLocaleString().replace(",", " ");
  };

  return (
    <Box>
      <Heading as="h2" sx={{ marginTop: "15px" }}>
        {selectInput.toUpperCase()}
      </Heading>
      <Paragraph as="p" sx={{ marginTop: "10px" }}>
        {selectInput === "Privatanställda arbetare"
          ? collection.privateEmployeeParagraph
          : selectInput === "Anställda inom kooperationen GTP"
            ? collection.employeesWithinTheCooperationParagraph
            : selectInput === "Anställda inom kooperationen KTP1 AFO-Handels"
              ? collection.employeesWithinTheCooperationKTPParagraph
              : selectInput === "Kommun och region AKAP-KR"
                ? collection.municipalityAndCountyCouncilParagraphAkapKr
                : selectInput === "Kommun och region KAP-KL"
                  ? collection.municipalityAndCountyCouncilParagraphKapKl
                  : null}
      </Paragraph>
      <Paragraph sx={{ margin: "10px 0" }}>{collection.midParagraph}</Paragraph>
      <Box
        as="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          overflow: "hidden",
        }}
      >
        <Box
          as="tbody"
          sx={{
            fontSize: "16px",
            verticalAlign: "top",
            "> tr": {
              backgroundColor: greenScale[1],
              height: "80px",
              display: "flex",
              width: "100%",
            },
            "> tr: nth-child(even)": {
              backgroundColor: primaryColorScales.greyScale[1],
              marginBottom: "20px",
            },
            "> tr > th": {
              fontWeight: "500",
              textAlign: "left",
              height: "100%",
              width: "60%",
              borderRight: "2px solid white",
              display: "flex",
              alignItems: "center",
              paddingLeft: "20px",
            },
            "> tr > td": {
              whiteSpace: "nowrap",
              width: "40%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              paddingLeft: "20px",
              fontFamily: "body",
            },
          }}
        >
          <Box as="tr">
            <Box as="th">{collection.averageMonthlySalary}</Box>
            <Box as="td">
              {averageMonthlySalary.toLocaleString().replace(",", " ") + " kr"}
            </Box>
          </Box>
          <Box as="tr">
            <Box as="th">{collection.yearlySalary}</Box>
            <Box as="td">
              {yearlySalary.toLocaleString().replace(",", " ") + " kr"}
            </Box>
          </Box>
          {selectInput === "Privatanställda arbetare" ||
          selectInput === "Anställda inom kooperationen GTP" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryUp}</Box>
              <Box as="td">
                {yearlySalary <= salaryLimit
                  ? yearlySalaryUpTo(yearlySalary).replace(",", " ") + " kr"
                  : fixedSumForMaxPremium.toLocaleString().replace(",", " ") +
                    " kr"}
              </Box>
            </Box>
          ) : selectInput === "Kommun och region AKAP-KR" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryUpAkapKr}</Box>
              <Box as="td">
                {yearlySalaryUpToAkap(yearlySalary).replace(",", "") + " kr"}
              </Box>
            </Box>
          ) : selectInput ===
            "Anställda inom kooperationen KTP1 AFO-Handels" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryUpKTP}</Box>
              <Box as="td">
                {yearlySalaryUpToKTP(yearlySalary).replace(",", "") + " kr"}
              </Box>
            </Box>
          ) : null}
          {selectInput === "Privatanställda arbetare" ||
          selectInput === "Anställda inom kooperationen GTP" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryOver}</Box>
              <Box as="td">
                {yearlySalary > salaryLimit
                  ? yearlySalaryOver(yearlySalary)
                  : 0 + " kr"}
              </Box>
            </Box>
          ) : selectInput === "Kommun och region AKAP-KR" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryOverAkapKr}</Box>
              <Box as="td">
                {yearlySalary > salaryLimit
                  ? yearlySalaryOverAkap(yearlySalary).replace(",", "") + " kr"
                  : 0 + " kr"}
              </Box>
            </Box>
          ) : selectInput ===
            "Anställda inom kooperationen KTP1 AFO-Handels" ? (
            <Box as="tr">
              <Box as="th">{collection.percentageOfYearlySalaryOverKTP}</Box>
              <Box as="td">
                {yearlySalary > salaryLimit
                  ? yearlySalaryOverKTP(yearlySalary).replace(",", "") + " kr"
                  : 0 + " kr"}
              </Box>
            </Box>
          ) : null}
          <Box as="tr">
            <Box as="th">{collection.yearlyPremium}</Box>
            <Box as="td">{annualPremium(yearlySalary) + " kr"}</Box>
          </Box>
        </Box>
      </Box>
      <Paragraph sx={{ margin: "20px 0" }}>{collection.endParagraph}</Paragraph>
    </Box>
  );
};
export default Table;
